/**
 * Admin Broadcast — rate-limited push composition.
 * Route: /admin/broadcast
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, AlertTriangle, Calendar } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

export default function AdminBroadcast() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState("all");
  const [scheduleAt, setScheduleAt] = useState("");
  const [dryResult, setDryResult] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/broadcast/log").then((r) => setLog(r.data || [])).catch(() => {});
  }, []);

  const dryRun = async () => {
    if (!title.trim() || !message.trim()) return toast.error("Fill title and message");
    try {
      const { data } = await apiClient.post("/admin/broadcast/dry-run", {
        title: title.trim(),
        message: message.trim(),
        target,
      });
      setDryResult(data);
    } catch {
      toast.error("Could not run pre-check");
    }
  };

  const send = async (confirmDuplicate = false) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/broadcast/send", {
        title: title.trim(),
        message: message.trim(),
        target,
        schedule_at: scheduleAt || null,
        confirm_duplicate: confirmDuplicate,
      });
      toast.success(`Sent to ${data.recipient_count} users`);
      setTitle("");
      setMessage("");
      setDryResult(null);
      apiClient.get("/admin/broadcast/log").then((r) => setLog(r.data || []));
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail === "Duplicate detected. Resubmit with confirm_duplicate=true to override.") {
        send(true);
        return;
      }
      toast.error(detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="px-4 py-10 text-center text-ink-soft text-[14px]" data-testid="not-admin">
        Admin only.
      </div>
    );
  }

  return (
    <div className="app-page" data-testid="admin-broadcast">
      <header className="sticky top-0 z-20 bg-canvas border-b border-line2">
        <div className="max-w-2xl mx-auto h-14 px-4 flex items-center gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" className="-ml-2 w-10 h-10 grid place-items-center">
            <ChevronLeft size={22} />
          </button>
          <h1 className="font-display text-[20px]">Broadcast</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4 pb-24 space-y-4">
        <Field label={`Title (${title.length}/60)`}>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 60))}
            data-testid="broadcast-title"
            className="w-full h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
          />
        </Field>
        <Field label={`Message (${message.length}/140)`}>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 140))}
            data-testid="broadcast-message"
            className="w-full p-3 rounded-[12px] border border-line bg-canvas2 outline-none"
            style={{ minHeight: 80 }}
          />
        </Field>
        <Field label="Target">
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="broadcast-target"
            className="w-full h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
          >
            <option value="all">All Users</option>
            <option value="city:Mumbai">Mumbai Hub</option>
            <option value="city:Bengaluru">Bengaluru Hub</option>
            <option value="city:Delhi">Delhi Hub</option>
            <option value="city:Pune">Pune Hub</option>
          </select>
        </Field>
        <Field label="Schedule (optional)">
          <input
            type="datetime-local"
            value={scheduleAt}
            onChange={(e) => setScheduleAt(e.target.value)}
            data-testid="broadcast-schedule"
            className="w-full h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
          />
        </Field>

        {/* Live preview */}
        <div className="bg-white rounded-[16px] p-4 border border-line2" data-testid="broadcast-preview">
          <div className="text-[10px] uppercase tracking-wider text-ink-tertiary font-medium">Preview</div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <DevicePreview os="iOS" title={title || "Title"} message={message || "Message body…"} />
            <DevicePreview os="Android" title={title || "Title"} message={message || "Message body…"} />
          </div>
        </div>

        {/* Dry run feedback */}
        {dryResult && (
          <div
            className="rounded-[14px] p-4"
            style={{
              background: dryResult.admin_limit_locked || dryResult.target_locked ? "#FCE6E6" : "#FFF1D1",
              border: "1px solid",
              borderColor: dryResult.admin_limit_locked || dryResult.target_locked ? "#F1B5B5" : "#F3D49E",
            }}
            data-testid="dry-run-result"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5" />
              <div className="text-[13px]">
                <div>
                  <strong>Audience:</strong> ~{dryResult.audience_estimate} users
                </div>
                <div>
                  <strong>Your daily limit:</strong> {dryResult.admin_limit_remaining}/3 remaining
                </div>
                {dryResult.target_locked && (
                  <div className="mt-1">
                    Target was notified recently. Next available:{" "}
                    {(dryResult.target_next_available || "").slice(0, 16).replace("T", " ")}
                  </div>
                )}
                {dryResult.is_duplicate && (
                  <div className="mt-1">
                    Similar broadcast was sent {(dryResult.duplicate_sent_at || "").slice(0, 16).replace("T", " ")}.
                    You can still send by tapping "Send Anyway".
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={dryRun}
            data-testid="broadcast-dryrun"
            className="flex-1 h-12 rounded-pill border border-line2 text-[13px]"
            style={{ borderRadius: 100 }}
          >
            Pre-check
          </button>
          <button
            onClick={() => send(dryResult?.is_duplicate)}
            disabled={busy || !title.trim() || !message.trim() || dryResult?.admin_limit_locked || dryResult?.target_locked}
            data-testid="broadcast-send"
            className="flex-1 h-12 rounded-pill disabled:opacity-50"
            style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
          >
            {busy ? "Sending…" : dryResult?.is_duplicate ? "Send Anyway" : "Send"}
          </button>
        </div>

        {/* Broadcast log */}
        <section data-testid="broadcast-log" className="mt-6">
          <h2 className="font-display text-[18px] mb-2">Broadcast log</h2>
          {log.length === 0 && (
            <div className="text-[13px] text-ink-soft">No broadcasts sent yet.</div>
          )}
          <div className="space-y-2">
            {log.map((b) => (
              <div
                key={b.broadcast_id}
                className="bg-white rounded-[12px] p-3 border border-line2"
                data-testid={`log-${b.broadcast_id}`}
              >
                <div className="text-[12px] text-ink-soft">
                  {(b.sent_at || "").slice(0, 16).replace("T", " ")} · {b.target} · {b.recipient_count} recipients
                </div>
                <div className="text-[14px] font-medium mt-0.5">{b.title}</div>
                <div className="text-[12px] text-ink-soft mt-0.5">{b.message}</div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function DevicePreview({ os, title, message }) {
  return (
    <div className="rounded-[12px] p-3" style={{ background: "#0B0B0B", color: "#fff" }}>
      <div className="text-[9px] uppercase tracking-wider text-white/50 mb-1">{os}</div>
      <div className="bg-white/10 rounded-[8px] p-2.5">
        <div className="text-[10px] text-white/70">FitFrik</div>
        <div className="text-[12px] font-medium mt-0.5 line-clamp-2">{title}</div>
        <div className="text-[11px] text-white/80 mt-0.5 line-clamp-2">{message}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium mb-1.5">{label}</div>
      {children}
    </div>
  );
}
